function normalizeRegistration(registration) {
  return (registration || '').toString().trim().toUpperCase().replace(/\s+/g, '');
}

function normalizeHex(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-F0-9]/g, '');
}

function toCallsign(registration) {
  return normalizeRegistration(registration).replace(/-/g, '');
}

function buildEndpointUrl(registration) {
  const template = process.env.REACT_APP_ADSB_ENDPOINT;
  if (!template) return null;

  const cleanReg = normalizeRegistration(registration);

  return template
    .replace('{registration}', encodeURIComponent(cleanReg))
    .replace('{reg}', encodeURIComponent(cleanReg));
}

export function buildAdsbMapUrl(adsbIcaoHex) {
  const hex = normalizeHex(adsbIcaoHex);
  if (!hex) return null;

  return `https://globe.adsbexchange.com/?icao=${encodeURIComponent(hex)}`;
}

function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function extractFromObject(item) {
  if (!item || typeof item !== 'object') return null;

  const lat = toNumber(firstDefined(item.lat, item.latitude, item.latitude_deg));
  const lng = toNumber(firstDefined(item.lon, item.lng, item.longitude, item.longitude_deg));

  if (lat === null || lng === null) return null;

  const altitude = toNumber(firstDefined(
    item.alt_baro,
    item.alt_geom,
    item.altitude,
    item.altitude_ft,
    item.baro_altitude
  ));

  const groundspeed = toNumber(firstDefined(
    item.gs,
    item.groundspeed,
    item.speed,
    item.velocity
  ));

  const heading = toNumber(firstDefined(
    item.track,
    item.heading,
    item.true_track,
    item.course
  ));

  const verticalSpeed = toNumber(firstDefined(
    item.baro_rate,
    item.geom_rate,
    item.vertical_rate,
    item.verticalSpeed
  ));

  const lastSeen = firstDefined(
    item.seen,
    item.lastSeen,
    item.last_seen,
    item.seen_pos,
    item.timestamp
  );

  const squawk = firstDefined(item.squawk, item.sqk);
  const callsign = firstDefined(item.flight, item.callsign, item.call);
  const hex = normalizeHex(firstDefined(item.hex, item.icao, item.icao24));

  return {
    lat,
    lng,
    altitude,
    groundspeed,
    verticalSpeed,
    heading,
    callsign: callsign ? String(callsign).trim() : '',
    registration: firstDefined(item.registration, item.reg, item.tail, item.r) || '',
    squawk: squawk ? String(squawk).trim() : '',
    lastSeen,
    seen: lastSeen,
    hex,
    source: item.source || 'adsb',
    airborne: inferAirborne({ altitude, groundspeed }),
    raw: item,
  };
}

function extractFromOpenSkyStateVector(row) {
  if (!Array.isArray(row) || row.length < 8) return null;

  const lng = toNumber(row[5]);
  const lat = toNumber(row[6]);

  if (lat === null || lng === null) return null;

  const altitude = toNumber(firstDefined(row[7], row[13]));
  const groundspeedMps = toNumber(row[9]);
  const groundspeed = groundspeedMps === null ? null : groundspeedMps * 1.94384;
  const heading = toNumber(row[10]);
  const verticalSpeedMps = toNumber(row[11]);
  const verticalSpeed = verticalSpeedMps === null ? null : verticalSpeedMps * 196.85;
  const onGround = row[8] === true;

  return {
    lat,
    lng,
    altitude,
    groundspeed,
    verticalSpeed,
    heading,
    callsign: String(row[1] || '').trim(),
    registration: '',
    squawk: String(row[14] || '').trim(),
    lastSeen: row[3] || null,
    seen: row[3] || null,
    hex: normalizeHex(row[0]),
    source: 'opensky',
    airborne: !onGround && inferAirborne({ altitude, groundspeed }),
    raw: row,
  };
}

function inferAirborne({ altitude, groundspeed }) {
  const alt = Number(altitude);
  const gs = Number(groundspeed);

  if (Number.isFinite(alt) && alt > 300) return true;
  if (Number.isFinite(gs) && gs > 45) return true;

  return false;
}

function findByRegistration(candidates, registration) {
  const target = normalizeRegistration(registration);
  const targetCallsign = toCallsign(registration);

  if (!Array.isArray(candidates) || !target) return null;

  for (const item of candidates) {
    const regValue = normalizeRegistration(
      item?.registration || item?.reg || item?.tail || item?.r
    );

    const callsignValue = normalizeRegistration(
      item?.callsign || item?.flight || item?.call
    );

    if ((regValue && regValue === target) || (callsignValue && callsignValue === targetCallsign)) {
      return item;
    }
  }

  return null;
}

function findOpenSkyStateByCallsign(states, registration) {
  if (!Array.isArray(states) || states.length === 0) return null;

  const callsign = toCallsign(registration);

  return states.find((row) => {
    if (!Array.isArray(row) || row.length < 2) return false;

    const rowCallsign = normalizeRegistration(row[1]);
    return rowCallsign === callsign;
  }) || null;
}

export async function fetchLiveAircraftPosition(registration) {
  const url = buildEndpointUrl(registration);
  if (!url) return null;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error('ADSB endpoint returned ' + response.status);
  }

  const json = await response.json();

  const candidatePools = [
    json?.aircraft,
    json?.ac,
    json?.data,
    json?.states,
  ];

  for (const pool of candidatePools) {
    if (Array.isArray(pool) && pool.length) {
      if (pool === json.states) {
        const matchingState = findOpenSkyStateByCallsign(pool, registration);
        if (!matchingState) continue;

        return extractFromOpenSkyStateVector(matchingState);
      }

      const match = findByRegistration(pool, registration);
      if (!match) continue;

      const extracted = extractFromObject(match);
      if (extracted) return extracted;
    }
  }

  return extractFromObject(json);
}